import type { EmailDirection, EmailOptionsActivity, EmailOptionsSettings, EmailTaskRule } from "./types";

export const directionText: Record<EmailDirection, { title: string; description: string }> = {
  inbound: {
    title: "Emails received",
    description: "Emails a customer sends to you or a colleague.",
  },
  outbound: {
    title: "Emails sent",
    description: "Emails you or a colleague send to a customer.",
  },
};

export const describeDueDays = (days: number): string => {
  if (days === 0) return "At the time of the email";
  return days === 1 ? "1 working day after the email" : `${days} working days after the email`;
};

const activityName = (activityId: string, activities: EmailOptionsActivity[]) =>
  activities.find(activity => activity.id === activityId)?.name ?? "task";

/** One plain-English sentence for what happens to emails in a direction. */
export const describeRule = (
  direction: EmailDirection,
  rule: EmailTaskRule,
  activities: EmailOptionsActivity[],
): string => {
  const subject = directionText[direction].title;
  if (!rule.activityId) return `${subject} are filed without a task.`;

  const activity = activityName(rule.activityId, activities);
  if (rule.completeTask) return `${subject} are logged as a completed "${activity}" task.`;

  return `${subject} create an open "${activity}" task, due ${describeDueDays(rule.dueDays).toLowerCase()}.`;
};

const isSameRule = (a: EmailTaskRule, b: EmailTaskRule) =>
  a.activityId === b.activityId && a.completeTask === b.completeTask && a.dueDays === b.dueDays;

export const isSameSettings = (a: EmailOptionsSettings, b: EmailOptionsSettings) =>
  a.autoCreateTask === b.autoCreateTask && isSameRule(a.inbound, b.inbound) && isSameRule(a.outbound, b.outbound);
